// p0-security-nov2: strip PII before the browser / app Sentry SDK sends
// anything. Wired in src/index.js (sendDefaultPii: false + these hooks).
// Removes: request bodies, cookies, Authorization-type headers, query strings
// (file URLs carry ?auth=<login token>), user ip/email, and GPS coordinates
// (lat/lng keys and "33.6012, -117.6723"-style pairs) from events,
// breadcrumbs and console-log breadcrumbs.

const FILTERED = "[Filtered]";

const SENSITIVE_HEADERS = new Set([
  "authorization", "proxy-authorization", "cookie", "set-cookie", "x-magic-secret",
  "x-storage-key", "x-api-key", "x-admin-key",
]);

const SENSITIVE_KEYS = new Set([
  "lat", "lng", "lon", "long", "latitude", "longitude", "coords", "coordinates",
  "location", "last_location", "parking", "geo", "position", "accuracy", "altitude",
  "password", "new_password", "current_password", "token", "auth", "auth_token",
  "admin_token", "session_token", "access_token", "id_token", "identity_token",
  "secret", "ticket", "magic", "authorization", "cookie",
]);

const SECRET_QS_RE = /([?&](?:auth|token|ticket|magic|session_token|access_token|id_token|code|key|secret|password)=)[^&#\s"']*/gi;
const GEO_QS_RE = /\b(lat|lng|lon|latitude|longitude|coords)=[^&#\s"']*/gi;
const COORD_PAIR_RE = /-?\d{1,3}\.\d{3,}\s*[,;/ ]\s*-?\d{1,3}\.\d{3,}/g;

export function scrubText(value) {
  if (typeof value !== "string") return value;
  return value
    .replace(SECRET_QS_RE, "$1[redacted]")
    .replace(GEO_QS_RE, "$1=[Filtered]")
    .replace(COORD_PAIR_RE, "[coords]");
}

function stripQuery(url) {
  if (typeof url !== "string") return url;
  const i = url.indexOf("?");
  return i === -1 ? url : url.slice(0, i);
}

export function scrubValue(value, depth = 0) {
  if (depth > 8 || value == null) return value;
  if (typeof value === "string") return scrubText(value);
  if (typeof value === "number") return value;
  if (Array.isArray(value)) {
    // console breadcrumbs keep raw args in data.arguments; a lone number next to
    // a coordinate-looking number is still GPS, so scrub numeric pairs too.
    const out = value.map((v) => scrubValue(v, depth + 1));
    for (let i = 0; i + 1 < out.length; i += 1) {
      const a = out[i];
      const b = out[i + 1];
      if (typeof a === "number" && typeof b === "number" && Math.abs(a) <= 90 && Math.abs(b) <= 180
          && !Number.isInteger(a) && !Number.isInteger(b)) {
        out[i] = FILTERED;
        out[i + 1] = FILTERED;
      }
    }
    return out;
  }
  if (typeof value === "object") {
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = SENSITIVE_KEYS.has(String(k).toLowerCase()) ? FILTERED : scrubValue(value[k], depth + 1);
    });
    return out;
  }
  return value;
}

function scrubRequest(req) {
  if (!req || typeof req !== "object") return req;
  const out = { ...req };
  delete out.data;
  delete out.cookies;
  if (out.query_string) out.query_string = FILTERED;
  if (out.url) out.url = stripQuery(out.url);
  if (out.headers && typeof out.headers === "object") {
    const h = {};
    Object.keys(out.headers).forEach((k) => {
      h[k] = SENSITIVE_HEADERS.has(k.toLowerCase()) ? FILTERED : scrubText(out.headers[k]);
    });
    out.headers = h;
  }
  return out;
}

export function scrubBreadcrumb(crumb) {
  if (!crumb || typeof crumb !== "object") return crumb;
  const out = { ...crumb };
  if (typeof out.message === "string") out.message = scrubText(out.message);
  if (out.data && typeof out.data === "object") {
    const data = scrubValue(out.data);
    if (typeof out.data.url === "string") data.url = stripQuery(out.data.url);
    if (typeof out.data.from === "string") data.from = stripQuery(scrubText(out.data.from));
    if (typeof out.data.to === "string") data.to = stripQuery(scrubText(out.data.to));
    out.data = data;
  }
  return out;
}

export function scrubEvent(event) {
  if (!event || typeof event !== "object") return event;
  const out = { ...event };
  if (out.request) out.request = scrubRequest(out.request);
  if (out.user && typeof out.user === "object") out.user = out.user.id ? { id: out.user.id } : {};
  ["extra", "contexts", "tags"].forEach((k) => {
    if (out[k] && typeof out[k] === "object") out[k] = scrubValue(out[k]);
  });
  if (typeof out.message === "string") out.message = scrubText(out.message);
  if (Array.isArray(out.breadcrumbs)) out.breadcrumbs = out.breadcrumbs.map(scrubBreadcrumb);
  if (out.exception && Array.isArray(out.exception.values)) {
    out.exception = {
      ...out.exception,
      values: out.exception.values.map((v) => (v && typeof v.value === "string" ? { ...v, value: scrubText(v.value) } : v)),
    };
  }
  return out;
}

export const sentryPrivacyOptions = {
  sendDefaultPii: false,
  beforeSend: (event) => scrubEvent(event),
  beforeSendTransaction: (event) => scrubEvent(event),
  beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
};
